const addEventListenerOnClickOnly = (element: any, callback: any, delta = 2) => {
    let startX: number;
    let startY: number;

    const mouseDownEvt = (event: any) => {
        startX = event.pageX;
        startY = event.pageY;
    };
    element.addEventListener('mousedown', mouseDownEvt);

    const mouseUpEvt = (event: any) => {
        const diffX = Math.abs(event.pageX - startX);
        const diffY = Math.abs(event.pageY - startY);

        if (diffX < delta && diffY < delta) {
            callback(event);
        }
    };
    element.addEventListener('mouseup', mouseUpEvt);

    return () => {
        element.removeEventListener('mousedown', mouseDownEvt);
        element.removeEventListener('mouseup', mouseUpEvt);
    };
};

// extract members of the object given a list of paths to extract
const extract = (obj: any, paths: string[]) => {

    const resolve = (obj: any, path: string[]) => {
        for (const p of path) {
            if (!obj.hasOwnProperty(p)) {
                return null;
            }
            obj = obj[p];
        }
        return obj;
    };

    const result: any = { };

    for (const pathString of paths) {
        const path = pathString.split('.');
        const value = resolve(obj, path);

        let parent = result;
        for (let i = 0; i < path.length; ++i) {
            const p = path[i];
            if (i < path.length - 1) {
                if (!parent.hasOwnProperty(p)) {
                    parent[p] = { };
                }
                parent = parent[p];
            } else {
                parent[p] = value;
            }
        }
    }

    return result;
};

export { addEventListenerOnClickOnly, extract };


/**
 * Returns the guest invitation token from the URL (`?token=...`), if any.
 * The ModelViewer has no Cognito session of its own, so it has to obtain
 * a JWT either from the URL (`?secureToken=...`, set by PanelAdmin) or by
 * exchanging the invitation UUID through `POST /user-auth/validateGuest`.
 * See `ensureGuestSession()`.
 */
const getGuestToken = (): string => {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        return (urlParams.get('token') || '').trim();
    } catch {
        return '';
    }
};

/**
 * Read the Cognito JWT that PanelAdmin forwards through `?secureToken=`.
 * When present, the viewer uses it directly without calling
 * `validateGuest`, since the JWT is already valid for the protected
 * Business API endpoints (`/damages/*`, `/photos`, `/cameras`, ...).
 */
const getSecureTokenFromUrl = (): string => {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        return (urlParams.get('secureToken') || '').trim();
    } catch {
        return '';
    }
};

/**
 * Cognito-issued JWT used as `Authorization: Bearer ...` for every
 * protected Business API call.
 *
 * Initialised from `?secureToken=` at module load (PanelAdmin embeds it
 * in the link when opening the viewer) and refreshed by
 * `ensureGuestSession()` if no `secureToken` is present but the URL has
 * an invitation `?token=` UUID instead.
 */
let guestIdToken: string | null = (() => {
    const fromUrl = getSecureTokenFromUrl();
    return fromUrl || null;
})();

/**
 * Resolve the auth API base URL from the current page's URL.
 *
 * The viewer ships at `/` for both PRE and PROD; we infer the right
 * Cognito-backed auth API by checking the same hint we already use for
 * `/api/requests/*` endpoints (the `?load=` parameter or hostname).
 */
const resolveAuthApiBaseUrl = (): string => {
    try {
        const search = window.location.search || '';
        const hostname = (window.location.hostname || '').toLowerCase();
        const isProd =
            hostname === '3dexpertise-vw.grupomobius.com' ||
            search.toLowerCase().includes('stage=prod') ||
            search.toLowerCase().includes('load=prod');
        return isProd
            ? 'https://d3yqg5brg7.execute-api.eu-west-1.amazonaws.com'
            : 'https://vo98xetz86.execute-api.eu-west-1.amazonaws.com';
    } catch {
        return 'https://vo98xetz86.execute-api.eu-west-1.amazonaws.com';
    }
};

/**
 * Exchange the URL guest-token UUID for a Cognito JWT (id_token) and keep
 * it in memory for subsequent API calls. Returns `true` when a token was
 * obtained or no exchange was needed (no `?token=` in the URL).
 *
 * Idempotent: once the JWT is stored further calls return immediately.
 */
const ensureGuestSession = async (): Promise<boolean> => {
    if (guestIdToken) return true;

    const guestToken = getGuestToken();
    if (!guestToken) return true; // No URL token → nothing to exchange.

    try {
        const response = await fetch(`${resolveAuthApiBaseUrl()}/user-auth/validateGuest`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ invitationToken: guestToken })
        });

        if (!response.ok) {
            console.warn(`[GuestAuth] validateGuest returned ${response.status}; protected endpoints will likely return 401.`);
            return false;
        }

        const data = await response.json().catch(() => null);
        // Prefer `id_token` over `access_token`: the Business API endpoints
        // are guarded by the Cognito User Pool authorizer, which accepts
        // id_tokens (audience = app client) but rejects access_tokens
        // (audience = resource server) with a 401.
        const idToken: string | undefined =
            data?.id_token ?? data?.idToken ?? data?.access_token ?? data?.accessToken;

        if (idToken) {
            guestIdToken = idToken;
            return true;
        }

        console.warn('[GuestAuth] validateGuest succeeded but did not return a JWT.');
        return false;
    } catch (error) {
        console.warn('[GuestAuth] validateGuest network error:', error);
        return false;
    }
};

/**
 * Build standard JSON request headers, automatically including the
 * Cognito JWT obtained from the guest-token exchange when one is
 * available. Falls back to the raw guest token in the unlikely case the
 * exchange has not happened yet — the backend will reject it, but we
 * still pass it through so the failure mode is "401 from Cognito" rather
 * than a silent empty Authorization.
 */
const buildAuthHeaders = (extra?: Record<string, string>): Record<string, string> => {
    const token = guestIdToken ?? getGuestToken();
    return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(extra || {})
    };
};

export { getGuestToken, buildAuthHeaders, ensureGuestSession };
